/**
 * SchoolTimetable — Full-featured with Physical/Online/Hybrid modes
 * Tabs: Weekly Grid | Teacher Schedule | Period Setup
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Loader2, Clock, Trash2, CalendarRange, Monitor, MapPin,
  Settings, Users, Video, Edit2, Wifi, Building, X, GripVertical,
  AlertTriangle, History, Download, ShieldCheck, RotateCcw
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { CampusHolidayBanner } from '@/components/school/CampusContext';
import { useAcademicContext } from '@/components/school/AcademicContext';
import WorkflowChecklist from '@/components/workflow/WorkflowChecklist';

const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };
const MODE_ICON = { physical: Building, online: Monitor, hybrid: Wifi };
const MODE_COLORS = {
  physical: 'bg-blue-50 border-blue-200 text-blue-700',
  online: 'bg-purple-50 border-purple-200 text-purple-700',
  hybrid: 'bg-teal-50 border-teal-200 text-teal-700',
};
const SLOT_COLORS = [
  'bg-indigo-50 border-indigo-200 text-indigo-800',
  'bg-emerald-50 border-emerald-200 text-emerald-800',
  'bg-amber-50 border-amber-200 text-amber-800',
  'bg-rose-50 border-rose-200 text-rose-800',
  'bg-sky-50 border-sky-200 text-sky-800',
  'bg-violet-50 border-violet-200 text-violet-800',
  'bg-teal-50 border-teal-200 text-teal-800',
];

// ========== SLOT DIALOG (Add/Edit) ==========
const SlotDialog = ({ open, onClose, tenantId, day, period, slot, subjects, teachers, classId, onSuccess, termId, yearId }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ subject_id: '', teacher_id: '', room: '', mode: 'physical', meeting_url: '', platform: '' });

  useEffect(() => {
    if (slot) {
      setForm({
        subject_id: slot.subject_id || '', teacher_id: slot.teacher_id || '',
        room: slot.room || '', mode: slot.mode || 'physical',
        meeting_url: slot.meeting_url || '', platform: slot.platform || '',
      });
    } else {
      setForm({ subject_id: '', teacher_id: '', room: '', mode: 'physical', meeting_url: '', platform: '' });
    }
  }, [slot, open]);

  const createMutation = useMutation({
    mutationFn: async (d) => { const { data } = await api.post(`/school/tenants/${tenantId}/timetable`, d); return data; },
    onSuccess: () => { toast.success('Slot added'); onSuccess?.(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: async (d) => { const { data } = await api.put(`/school/tenants/${tenantId}/timetable/${slot.slot_id}`, d); return data; },
    onSuccess: () => { toast.success('Slot updated'); onSuccess?.(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = () => {
    if (slot?.slot_id) {
      updateMutation.mutate(form);
    } else {
      createMutation.mutate({ ...form, class_id: classId, day, period, term_id: termId || null, academic_year_id: yearId || null });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isOnline = form.mode === 'online' || form.mode === 'hybrid';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{slot?.slot_id ? 'Edit' : 'Add'} Timetable Slot</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 capitalize">{day} — {period}</p>
        <div className="space-y-3 mt-2">
          {/* Mode selector */}
          <div>
            <Label className="mb-1.5 block">Class Mode *</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {['physical', 'online', 'hybrid'].map(m => {
                const Icon = MODE_ICON[m];
                return (
                  <button key={m} onClick={() => setForm({ ...form, mode: m })}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium capitalize transition-all ${form.mode === m ? MODE_COLORS[m] + ' border-2 shadow-sm' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                    data-testid={`mode-${m}`}>
                    <Icon className="h-3.5 w-3.5" /> {m}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Subject *</Label>
            <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
              <SelectTrigger data-testid="slot-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
              <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label>Teacher *</Label>
            <Select value={form.teacher_id} onValueChange={v => setForm({ ...form, teacher_id: v })}>
              <SelectTrigger data-testid="slot-teacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
              <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {(form.mode === 'physical' || form.mode === 'hybrid') && (
            <div>
              <Label>Room</Label>
              <Input value={form.room} onChange={e => setForm({ ...form, room: e.target.value })} placeholder="e.g. Room 101" data-testid="slot-room" />
            </div>
          )}

          {isOnline && (
            <>
              <div>
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm({ ...form, platform: v })}>
                  <SelectTrigger data-testid="slot-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="gmeet">Google Meet</SelectItem>
                    <SelectItem value="teams">MS Teams</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Meeting URL</Label>
                <Input value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://zoom.us/j/..." data-testid="slot-meeting-url" />
              </div>
            </>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending || !form.subject_id || !form.teacher_id} data-testid="submit-slot-btn">
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{slot?.slot_id ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== WEEKLY GRID ==========
const WeeklyGrid = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { termId, yearId, term, semesters } = useAcademicContext(tenantId);
  const [selectedClass, setSelectedClass] = useState('');
  const [slotDialog, setSlotDialog] = useState({ open: false, day: '', period: '', slot: null });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId, yearId, termId],
    queryFn: async () => {
      const { data } = await api.get(`/school/tenants/${tenantId}/classes`,
        { params: { ...(yearId ? { academic_year_id: yearId } : {}), ...(termId ? { term_id: termId } : {}) } });
      return data;
    },
  });
  const { data: subjects = [] } = useQuery({
    queryKey: ['school-subjects', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/subjects`); return data; },
  });
  const { data: teachers = [] } = useQuery({
    queryKey: ['school-teachers', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/teachers`); return data; },
  });
  const { data: weeklyData, isLoading } = useQuery({
    queryKey: ['school-timetable-weekly', tenantId, selectedClass, termId],
    queryFn: async () => {
      const { data } = await api.get(`/school/tenants/${tenantId}/timetable/weekly/${selectedClass}`,
        { params: termId ? { term_id: termId } : {} });
      return data;
    },
    enabled: !!selectedClass,
  });
  const { data: stats } = useQuery({
    queryKey: ['timetable-stats', tenantId, termId],
    queryFn: async () => {
      const { data } = await api.get(`/school/tenants/${tenantId}/timetable/stats`,
        { params: termId ? { term_id: termId } : {} });
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => { const { data } = await api.delete(`/school/tenants/${tenantId}/timetable/${id}`); return data; },
    onSuccess: () => { toast.success('Slot removed'); queryClient.invalidateQueries({ queryKey: ['school-timetable-weekly'] }); queryClient.invalidateQueries({ queryKey: ['timetable-stats'] }); },
  });

  const subjectColorMap = useMemo(() => {
    const map = {};
    subjects.forEach((s, i) => { map[s.name] = SLOT_COLORS[i % SLOT_COLORS.length]; });
    return map;
  }, [subjects]);

  const periods = weeklyData?.periods || [];
  const days = weeklyData?.include_saturday ? Object.keys(DAY_LABELS) : Object.keys(DAY_LABELS).slice(0, 5);

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Slots', value: stats?.total_slots || 0, color: 'text-slate-900' },
          { label: 'Physical', value: stats?.physical || 0, icon: Building, color: 'text-blue-600' },
          { label: 'Online', value: stats?.online || 0, icon: Monitor, color: 'text-purple-600' },
          { label: 'Hybrid', value: stats?.hybrid || 0, icon: Wifi, color: 'text-teal-600' },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-3 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1">{s.icon && <s.icon className="h-3 w-3" />}{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {semesters.length > 0 && (
        <div className="flex items-center gap-2 text-xs rounded-md border bg-violet-50 border-violet-200 text-violet-800 px-3 py-2"
             data-testid="timetable-semester-banner">
          <CalendarRange className="h-3.5 w-3.5" />
          <span>
            Building the timetable for <b>{term?.name || 'this semester'}</b>. Each semester keeps its
            own timetable — switch semesters in the bar above to view or build another.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="min-w-0 sm:min-w-[220px]">
          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger data-testid="timetable-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Building className="h-3 w-3 text-blue-500" /> Physical</span>
          <span className="flex items-center gap-1"><Monitor className="h-3 w-3 text-purple-500" /> Online</span>
          <span className="flex items-center gap-1"><Wifi className="h-3 w-3 text-teal-500" /> Hybrid</span>
        </div>
      </div>

      {!selectedClass ? (
        <Card><CardContent className="py-16 text-center text-slate-400">Select a class to view timetable</CardContent></Card>
      ) : isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="timetable-grid">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-2 border border-slate-200 text-left text-sm font-medium text-slate-600 w-full max-w-[100px]"><Clock className="h-3.5 w-3.5 inline mr-1" /> Period</th>
                  {days.map(d => (
                    <th key={d} className="p-2 border border-slate-200 text-center text-sm font-medium text-slate-600">{DAY_LABELS[d]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map(period => (
                  <tr key={period.name} className={period.is_break ? 'bg-amber-50/30' : ''}>
                    <td className="p-1.5 border border-slate-200">
                      <div className="text-xs font-medium text-slate-700">{period.name}</div>
                      <div className="text-[10px] text-slate-400">{period.start}-{period.end}</div>
                    </td>
                    {days.map(day => {
                      if (period.is_break) {
                        return <td key={day} className="p-1 border border-slate-200 text-center"><span className="text-[10px] text-slate-400 italic">Break</span></td>;
                      }
                      const slot = weeklyData?.timetable?.[day]?.find(s => s.period === period.name);
                      if (!slot || !slot.subject_name) {
                        return (
                          <td key={day} className="p-0.5 border border-slate-200">
                            <button onClick={() => setSlotDialog({ open: true, day, period: period.name, slot: null })}
                              className="w-full h-full min-h-[52px] rounded border-2 border-dashed border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors flex items-center justify-center"
                              data-testid={`add-slot-${day}-${period.name}`}>
                              <Plus className="h-3.5 w-3.5 text-slate-300" />
                            </button>
                          </td>
                        );
                      }
                      const ModeIcon = MODE_ICON[slot.mode] || Building;
                      const colorCls = subjectColorMap[slot.subject_name] || SLOT_COLORS[0];
                      return (
                        <td key={day} className="p-0.5 border border-slate-200">
                          <div className={`rounded p-1.5 border ${colorCls} relative group min-h-[52px] cursor-pointer`}
                            onClick={() => setSlotDialog({ open: true, day, period: period.name, slot })}
                            data-testid={`slot-${day}-${period.name}`}>
                            <div className="flex items-center gap-1">
                              <ModeIcon className="h-3 w-3 opacity-60 flex-shrink-0" />
                              <span className="text-xs font-semibold truncate">{slot.subject_code || slot.subject_name}</span>
                            </div>
                            <div className="text-[10px] opacity-75 truncate">{slot.teacher_name}</div>
                            {slot.room && <div className="text-[10px] opacity-60">Rm: {slot.room}</div>}
                            {slot.meeting_url && <Video className="h-2.5 w-2.5 absolute bottom-1 right-1 opacity-40" />}
                            <button onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(slot.slot_id); }}
                              className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-white/50"
                              data-testid={`delete-slot-${day}-${period.name}`}>
                              <Trash2 className="h-2.5 w-2.5 text-red-500" />
                            </button>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <SlotDialog
        open={slotDialog.open} onClose={() => setSlotDialog({ open: false, day: '', period: '', slot: null })}
        tenantId={tenantId} day={slotDialog.day} period={slotDialog.period} slot={slotDialog.slot}
        subjects={subjects} teachers={teachers} classId={selectedClass}
        termId={termId} yearId={yearId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['school-timetable-weekly'] });
          queryClient.invalidateQueries({ queryKey: ['timetable-stats'] });
          queryClient.invalidateQueries({ queryKey: ['school-academic-structure'] });
        }}
      />
    </div>
  );
};

// ========== TEACHER SCHEDULE ==========
const TeacherSchedule = ({ tenantId }) => {
  const { termId } = useAcademicContext(tenantId);
  const [selectedTeacher, setSelectedTeacher] = useState('');

  const { data: teachers = [] } = useQuery({
    queryKey: ['school-teachers', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/teachers`); return data; },
  });

  const { data: scheduleData, isLoading } = useQuery({
    queryKey: ['teacher-schedule', tenantId, selectedTeacher, termId],
    queryFn: async () => {
      const { data } = await api.get(`/school/tenants/${tenantId}/timetable/teacher/${selectedTeacher}`,
        { params: termId ? { term_id: termId } : {} });
      return data;
    },
    enabled: !!selectedTeacher,
  });

  const periods = scheduleData?.periods || [];
  const days = scheduleData?.include_saturday ? Object.keys(DAY_LABELS) : Object.keys(DAY_LABELS).slice(0, 5);

  return (
    <div className="space-y-4" data-testid="teacher-schedule">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Teacher Schedule</h3>
          {scheduleData && <p className="text-xs text-slate-500">{scheduleData.total_periods} teaching periods/week</p>}
        </div>
        <div className="min-w-0 sm:min-w-[220px]">
          <Select value={selectedTeacher} onValueChange={setSelectedTeacher}>
            <SelectTrigger data-testid="teacher-schedule-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
            <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!selectedTeacher ? (
        <Card><CardContent className="py-16 text-center text-slate-400">Select a teacher to view schedule</CardContent></Card>
      ) : isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-2 border border-slate-200 text-left text-sm font-medium text-slate-600 w-full max-w-[100px]">Period</th>
                  {days.map(d => <th key={d} className="p-2 border border-slate-200 text-center text-sm font-medium text-slate-600">{DAY_LABELS[d]}</th>)}
                </tr>
              </thead>
              <tbody>
                {periods.map(period => (
                  <tr key={period.name} className={period.is_break ? 'bg-amber-50/30' : ''}>
                    <td className="p-1.5 border border-slate-200">
                      <div className="text-xs font-medium text-slate-700">{period.name}</div>
                      <div className="text-[10px] text-slate-400">{period.start}-{period.end}</div>
                    </td>
                    {days.map(day => {
                      if (period.is_break) return <td key={day} className="p-1 border border-slate-200 text-center"><span className="text-[10px] text-slate-400 italic">Break</span></td>;
                      const slot = scheduleData?.schedule?.[day]?.find(s => s.period === period.name);
                      if (!slot || slot.is_free) {
                        return <td key={day} className="p-1 border border-slate-200"><div className="min-h-[40px] rounded bg-green-50 flex items-center justify-center"><span className="text-[10px] text-green-400">Free</span></div></td>;
                      }
                      const ModeIcon = MODE_ICON[slot.mode] || Building;
                      return (
                        <td key={day} className="p-0.5 border border-slate-200">
                          <div className="rounded p-1.5 bg-indigo-50 border border-indigo-200 min-h-[40px]">
                            <div className="flex items-center gap-1">
                              <ModeIcon className="h-3 w-3 text-indigo-400" />
                              <span className="text-xs font-semibold text-indigo-800 truncate">{slot.class_name}</span>
                            </div>
                            <div className="text-[10px] text-indigo-600 truncate">{slot.subject_name}</div>
                            {slot.room && <div className="text-[10px] text-indigo-400">Rm: {slot.room}</div>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

// ========== PERIOD SETUP ==========
const PeriodSetup = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [periods, setPeriods] = useState([]);
  const [includeSaturday, setIncludeSaturday] = useState(false);

  const { data: periodsData } = useQuery({
    queryKey: ['timetable-periods', tenantId],
    queryFn: async () => { const { data } = await api.get(`/school/tenants/${tenantId}/timetable/periods`); return data; },
  });

  useEffect(() => {
    if (periodsData) {
      setPeriods(periodsData.periods || []);
      setIncludeSaturday(periodsData.include_saturday || false);
    }
  }, [periodsData]);

  const saveMutation = useMutation({
    mutationFn: async (d) => { const { data } = await api.put(`/school/tenants/${tenantId}/timetable/periods`, d); return data; },
    onSuccess: () => {
      toast.success('Periods saved');
      queryClient.invalidateQueries({ queryKey: ['timetable-periods'] });
      queryClient.invalidateQueries({ queryKey: ['school-timetable-weekly'] });
      queryClient.invalidateQueries({ queryKey: ['teacher-schedule'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const addPeriod = () => {
    const lastEnd = periods.length > 0 ? periods[periods.length - 1].end : '07:30';
    setPeriods([...periods, { name: `Period ${periods.length + 1}`, start: lastEnd, end: '', is_break: false }]);
  };

  const addBreak = () => {
    const lastEnd = periods.length > 0 ? periods[periods.length - 1].end : '09:00';
    setPeriods([...periods, { name: 'Break', start: lastEnd, end: '', is_break: true }]);
  };

  const updatePeriod = (i, field, value) => {
    setPeriods(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: field === 'is_break' ? value === 'true' : value } : p));
  };

  const removePeriod = (i) => setPeriods(prev => prev.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4 max-w-3xl" data-testid="period-setup">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Period Definitions</h3>
          <p className="text-xs text-slate-500">Define the daily periods, breaks, and their timings</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5">
            <Label className="text-sm">Include Saturday</Label>
            <Switch checked={includeSaturday} onCheckedChange={setIncludeSaturday} data-testid="saturday-toggle" />
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-2">
          {periods.map((p, i) => (
            <div key={i} className={`flex items-center gap-2 p-2 rounded-lg ${p.is_break ? 'bg-amber-50' : 'bg-slate-50'}`} data-testid={`period-row-${i}`}>
              <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
              <Input className="h-8 w-full max-w-[140px] text-sm" value={p.name} onChange={e => updatePeriod(i, 'name', e.target.value)} placeholder="Period name" />
              <Input type="time" className="h-8 w-full max-w-[110px] text-sm" value={p.start} onChange={e => updatePeriod(i, 'start', e.target.value)} />
              <span className="text-slate-400 text-sm">to</span>
              <Input type="time" className="h-8 w-full max-w-[110px] text-sm" value={p.end} onChange={e => updatePeriod(i, 'end', e.target.value)} />
              <Badge variant={p.is_break ? 'secondary' : 'outline'} className="cursor-pointer text-xs"
                onClick={() => updatePeriod(i, 'is_break', p.is_break ? 'false' : 'true')}>
                {p.is_break ? 'Break' : 'Class'}
              </Badge>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removePeriod(i)}><X className="h-3.5 w-3.5 text-red-400" /></Button>
            </div>
          ))}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={addPeriod} data-testid="add-period-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Period</Button>
            <Button variant="outline" size="sm" onClick={addBreak} data-testid="add-break-btn"><Clock className="h-3.5 w-3.5 mr-1" /> Add Break</Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => saveMutation.mutate({ periods, include_saturday: includeSaturday })} disabled={saveMutation.isPending} data-testid="save-periods-btn">
          {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Periods
        </Button>
      </div>
    </div>
  );
};

// ========== PHASE C: CONFLICTS ==========
const ConflictsTab = ({ tenantId }) => {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['timetable-conflicts', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/timetable/conflicts`).then(r => r.data),
  });
  const conflicts = data?.conflicts || [];
  const color = { teacher_clash: 'bg-rose-50 border-rose-200 text-rose-700', room_clash: 'bg-amber-50 border-amber-200 text-amber-700', class_clash: 'bg-purple-50 border-purple-200 text-purple-700' };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Timetable Conflicts</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Detects teacher, room & class clashes across the full week.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="conflicts-refresh-btn">
          {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Re-scan
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> :
         conflicts.length === 0 ? (
          <div className="text-center py-10 text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="conflicts-empty">
            <ShieldCheck className="h-8 w-8 mx-auto mb-2" />
            <p className="font-medium">All clear — no conflicts detected.</p>
          </div>
         ) : (
          <div className="space-y-2" data-testid="conflicts-list">
            <p className="text-sm"><span className="font-semibold text-rose-700">{data.conflict_count}</span> conflict(s) found</p>
            {conflicts.map((c, i) => (
              <div key={i} className={`p-3 rounded-md border ${color[c.type] || 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium capitalize">{c.type.replace('_',' ')}</span>
                  <Badge variant="outline" className="text-xs">{c.day} · Period {c.period}</Badge>
                </div>
                <div className="text-xs">{c.teacher_id && `Teacher: ${c.teacher_id}`}{c.room && `Room: ${c.room}`}{c.class_id && `Class: ${c.class_id}`} — {c.slots.length} overlapping slots</div>
              </div>
            ))}
          </div>
         )}
      </CardContent>
    </Card>
  );
};

// ========== PHASE C: VERSIONS ==========
const VersionsTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['timetable-versions', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/timetable/versions`).then(r => r.data),
  });
  const snapMut = useMutation({
    mutationFn: (body) => api.post(`/api/school/tenants/${tenantId}/timetable/versions/snapshot`, body),
    onSuccess: () => { toast.success('Snapshot saved'); setOpen(false); setLabel(''); setNotes(''); qc.invalidateQueries({ queryKey: ['timetable-versions', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to snapshot'),
  });
  const restoreMut = useMutation({
    mutationFn: (id) => api.post(`/api/school/tenants/${tenantId}/timetable/versions/${id}/restore`),
    onSuccess: (r) => { toast.success(`Restored ${r.data.restored_slots} slot(s) — an auto-backup of the previous state was created.`); qc.invalidateQueries(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to restore'),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/api/school/tenants/${tenantId}/timetable/versions/${id}`),
    onSuccess: () => { toast.success('Version deleted'); qc.invalidateQueries({ queryKey: ['timetable-versions', tenantId] }); },
  });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><History className="h-5 w-5 text-indigo-600" /> Timetable Versions</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Save labelled snapshots of your current timetable. Restore any one with a single click.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="version-snapshot-btn"><Plus className="h-4 w-4 mr-1" /> New Snapshot</Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div> :
         versions.length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="versions-empty">No snapshots yet.</p> :
         <Table>
           <TableHeader><TableRow><TableHead>Label</TableHead><TableHead>Slots</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
           <TableBody>
            {versions.map(v => (
              <TableRow key={v.id} data-testid={`version-row-${v.id}`}>
                <TableCell className="font-medium">{v.label}{v.notes && <div className="text-xs text-slate-500">{v.notes}</div>}</TableCell>
                <TableCell><Badge variant="secondary">{v.slot_count}</Badge></TableCell>
                <TableCell className="text-xs text-slate-600">{v.created_at ? new Date(v.created_at).toLocaleString() : '—'}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => { if (confirm('Restore this snapshot? Current timetable will be auto-backed up first.')) restoreMut.mutate(v.id); }} data-testid={`version-restore-${v.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('Delete this snapshot?')) delMut.mutate(v.id); }} data-testid={`version-delete-${v.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
           </TableBody>
         </Table>}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Snapshot</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Label *</Label><Input placeholder="e.g. Pre-exam week schedule" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="version-label-input" /></div>
            <div><Label>Notes</Label><Textarea placeholder="Optional notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="version-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => snapMut.mutate({ label, notes })} disabled={!label || snapMut.isPending} data-testid="version-save-btn">
              {snapMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save Snapshot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ========== PHASE C: EXPORT ==========
const ExportTab = ({ tenantId }) => {
  const [loading, setLoading] = useState(false);
  const doExport = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/school/tenants/${tenantId}/timetable/export/csv`);
      const blob = new Blob([r.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `timetable-${tenantId}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.rows} rows`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setLoading(false); }
  };
  const doPrint = () => { window.print(); };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Download className="h-5 w-5 text-emerald-600" /> Export & Print</CardTitle>
        <p className="text-sm text-slate-500 mt-1">Download the full timetable as CSV or send the current view to your printer.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={doExport} disabled={loading} data-testid="export-csv-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Download CSV
        </Button>
        <Button variant="outline" onClick={doPrint} className="ml-2" data-testid="print-timetable-btn">
          <Monitor className="h-4 w-4 mr-1" /> Print Weekly Grid
        </Button>
      </CardContent>
    </Card>
  );
};

// ========== MAIN COMPONENT ==========
// ========== READINESS PANEL ==========
// Thin wrapper that reads /school/workflow/timetable/{tid}/readiness and
// renders the shared WorkflowChecklist card. Timetable has no publish
// toggle — readiness is informational so admins know what's missing
// (classes without slots, double-bookings, etc.).
const TimetableReadiness = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['timetable-readiness', tenantId],
    queryFn: () => api.get(`/school/workflow/timetable/${tenantId}/readiness`).then((r) => r.data),
    enabled: !!tenantId,
    refetchOnWindowFocus: true,
  });
  return (
    <WorkflowChecklist
      data={data}
      loading={isLoading}
      className="mb-4"
      data-testid="timetable-readiness-card"
    />
  );
};

export const SchoolTimetable = ({ tenantId }) => {
  return (
    <div data-testid="timetable-panel">
      <CampusHolidayBanner tenantId={tenantId} />
      <Tabs defaultValue="weekly" className="w-full">
        <TabsList className="mb-4 h-auto gap-1 flex-wrap">
          <TabsTrigger value="weekly" className="text-xs" data-testid="tab-weekly"><CalendarRange className="h-3.5 w-3.5 mr-1" /> Weekly Grid</TabsTrigger>
          <TabsTrigger value="teacher" className="text-xs" data-testid="tab-teacher"><Users className="h-3.5 w-3.5 mr-1" /> Teacher Schedule</TabsTrigger>
          <TabsTrigger value="periods" className="text-xs" data-testid="tab-periods"><Settings className="h-3.5 w-3.5 mr-1" /> Period Setup</TabsTrigger>
          <TabsTrigger value="conflicts" className="text-xs" data-testid="tab-conflicts"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Conflicts</TabsTrigger>
          <TabsTrigger value="readiness" className="text-xs" data-testid="tab-readiness"><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Readiness</TabsTrigger>
          <TabsTrigger value="versions" className="text-xs" data-testid="tab-versions"><History className="h-3.5 w-3.5 mr-1" /> Versions</TabsTrigger>
          <TabsTrigger value="export" className="text-xs" data-testid="tab-export"><Download className="h-3.5 w-3.5 mr-1" /> Export</TabsTrigger>
        </TabsList>
        <TabsContent value="weekly">
          <TimetableReadiness tenantId={tenantId} />
          <WeeklyGrid tenantId={tenantId} />
        </TabsContent>
        <TabsContent value="teacher"><TeacherSchedule tenantId={tenantId} /></TabsContent>
        <TabsContent value="periods"><PeriodSetup tenantId={tenantId} /></TabsContent>
        <TabsContent value="conflicts"><ConflictsTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="readiness"><TimetableReadiness tenantId={tenantId} /></TabsContent>
        <TabsContent value="versions"><VersionsTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="export"><ExportTab tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolTimetable;
